import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check, Clock, FileText, MessageCircle, Shield, Workflow } from 'lucide-react';
import { Accordion, Badge, Breadcrumbs, Button, Card } from '../../components';
import useDocumentHead from '../../hooks/useDocumentHead';
import { useLang, type Lang } from '../../i18n';
import { get } from '../../services/api';
import LeadFormModal from '../LeadFormModal';
import './ServiceDetailPage.scss';

interface Package { name?: string; title?: string; price?: string; price_from?: string; features?: string[] }
interface FaqItem { id: string | number; question: string; answer: string }
interface RelatedCase { slug: string; title: string }
interface ServiceCategoryRef { slug?: string; title?: string }
interface Service {
  slug: string;
  title: string;
  short_description?: string;
  description?: string;
  long_description?: string;
  bullets?: string[];
  packages?: Package[];
  faq_items?: FaqItem[];
  related_cases?: RelatedCase[];
  category?: ServiceCategoryRef | string;
  category_slug?: string;
}

const LANG_MAP: Record<Lang, string> = { 'uz-Latn': 'uz_lat', 'uz-Cyrl': 'uz_cyr', ru: 'ru', en: 'en' };

export default function ServiceDetailPage() {
  const { t, lang } = useLang();
  const { category: categoryParam = '', slug = '' } = useParams();
  const [data, setData] = useState<Service | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await get<Service>(`/services/${slug}/?lang=${LANG_MAP[lang]}`);
        if (!cancel) setData(res);
      } catch {
        if (!cancel) setData(null);
      }
    })();
    return () => { cancel = true; };
  }, [slug, lang]);

  const title = data?.title || slug;
  const shortDesc = data?.short_description || data?.description;
  const categoryLabel = typeof data?.category === 'object' ? (data.category?.title || data.category?.slug || categoryParam) : (data?.category || categoryParam);
  const categorySlug = data?.category_slug || (typeof data?.category === 'object' ? data.category?.slug : data?.category) || categoryParam;

  const schemaList: Array<Record<string, unknown>> = [
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('brand.name'), item: 'https://ukam.uz/' },
      { '@type': 'ListItem', position: 2, name: t('services.title'), item: 'https://ukam.uz/services' },
      { '@type': 'ListItem', position: 3, name: categoryLabel, item: `https://ukam.uz/services?category=${categorySlug}` },
      { '@type': 'ListItem', position: 4, name: title, item: `https://ukam.uz/services/${categorySlug}/${slug}` },
    ] },
    { '@context': 'https://schema.org', '@type': 'Service',
      name: title, description: shortDesc,
      provider: { '@type': 'Organization', name: 'Ukam IT', url: 'https://ukam.uz/' },
      offers: (data?.packages || []).map((p) => ({ '@type': 'Offer', name: p.name || p.title, price: p.price || p.price_from })),
    },
  ];
  if (data?.faq_items?.length) {
    schemaList.push({ '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: data.faq_items.map((f) => ({
        '@type': 'Question', name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  useDocumentHead({
    title: `${title} — ${t('brand.name')}`,
    description: shortDesc || t('services.sub'),
    canonical: `https://ukam.uz/services/${categorySlug}/${slug}`,
    og: { title, description: shortDesc, type: 'website' },
    schemaOrg: schemaList,
  });

  const processSteps = [
    { icon: <MessageCircle size={18} />, t: t('service.process.s1.t'), d: t('service.process.s1.d') },
    { icon: <FileText size={18} />, t: t('service.process.s2.t'), d: t('service.process.s2.d') },
    { icon: <Workflow size={18} />, t: t('service.process.s3.t'), d: t('service.process.s3.d') },
    { icon: <Shield size={18} />, t: t('service.process.s4.t'), d: t('service.process.s4.d') },
    { icon: <Clock size={18} />, t: t('service.process.s5.t'), d: t('service.process.s5.d') },
  ];

  return (
    <div className="service-detail">
      <section className="service-detail__hero">
        <div className="container">
          <Breadcrumbs items={[
            { label: t('brand.name'), href: '/' },
            { label: t('services.title'), href: '/services' },
            { label: String(categoryLabel), href: `/services?category=${categorySlug}` },
            { label: title },
          ]} />
          <h1 className="service-detail__title">{title}</h1>
          {shortDesc && <p className="service-detail__desc">{shortDesc}</p>}
          <div className="service-detail__hero-actions">
            <Button variant="primary" size="lg" onClick={() => setOpen(true)} iconRight={<ArrowRight size={18} />}>
              {t('service.order')}
            </Button>
            <Link to="#consult" className="service-detail__hero-link">{t('cta.consultation')}</Link>
          </div>
        </div>
      </section>

      {data?.long_description && (
        <section className="section service-detail__body">
          <div className="container">
            <h2 className="service-detail__h2">{t('service.about')}</h2>
            <div className="service-detail__prose">
              {data.long_description.split(/\n\n+/).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {data?.bullets && data.bullets.length > 0 && (
        <section className="section service-detail__bullets-sec">
          <div className="container">
            <h2 className="service-detail__h2">{t('service.bullets')}</h2>
            <ul className="service-detail__bullets">
              {data.bullets.map((b, i) => (
                <li key={i}><Check size={16} /><span>{b}</span></li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Процесс — статический, понятный, всегда показываем как часть «полноценного» окна услуги */}
      <section className="section service-detail__process">
        <div className="container">
          <h2 className="service-detail__h2">{t('service.process.t')}</h2>
          <ol className="service-detail__steps">
            {processSteps.map((s, i) => (
              <li key={i} className="service-detail__step">
                <span className="service-detail__step-num">{String(i + 1).padStart(2, '0')}</span>
                <span className="service-detail__step-icon">{s.icon}</span>
                <div>
                  <h3 className="service-detail__step-title">{s.t}</h3>
                  <p className="service-detail__step-desc">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {data?.packages && data.packages.length > 0 && (
        <section className="section service-detail__packages-sec">
          <div className="container">
            <h2 className="service-detail__h2">{t('service.packages')}</h2>
            <div className="grid grid--3">
              {data.packages.map((p, i) => (
                <Card key={i} variant="elevated" padding="lg" className="service-detail__pkg">
                  <h3 className="service-detail__pkg-name">{p.name || p.title}</h3>
                  {(p.price || p.price_from) && <div className="service-detail__pkg-price">{p.price || p.price_from}</div>}
                  <ul className="service-detail__bullets">
                    {(p.features || []).map((f, j) => (
                      <li key={j}><Check size={14} /><span>{f}</span></li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {data?.faq_items && data.faq_items.length > 0 && (
        <section className="section">
          <div className="container">
            <h2 className="service-detail__h2">{t('home.faq.title')}</h2>
            <Accordion items={data.faq_items.map((f) => ({ id: String(f.id), title: f.question, content: f.answer }))} />
          </div>
        </section>
      )}

      {data?.related_cases && data.related_cases.length > 0 && (
        <section className="section">
          <div className="container">
            <h2 className="service-detail__h2">{t('service.related_cases')}</h2>
            <div className="grid grid--3">
              {data.related_cases.map((c) => (
                <Link key={c.slug} to={`/cases/${c.slug}`} className="service-detail__case">
                  <Card variant="bordered" padding="md">
                    <Badge variant="neutral">{t('cases.title')}</Badge>
                    <h3 className="service-detail__case-title">{c.title}</h3>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="consult" className="section service-detail__cta">
        <div className="container">
          <Card variant="elevated" padding="lg" className="service-detail__cta-card">
            <div>
              <h2 className="service-detail__h2">{t('service.consult')}</h2>
              <p className="service-detail__cta-desc">{t('service.consult_desc')}</p>
            </div>
            <Button variant="primary" size="lg" onClick={() => setOpen(true)} iconRight={<ArrowRight size={18} />}>
              {t('cta.consultation')}
            </Button>
          </Card>
        </div>
      </section>
      <LeadFormModal open={open} onClose={() => setOpen(false)} defaultServiceSlug={slug} />
    </div>
  );
}
